/**
 * Shared bits for the AI-powered free tools: a tiny hook to call /api/tools/*
 * with limit handling, plus a copy button, a platform selector and the
 * "limit reached / create account" funnel CTA.
 */
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Copy, Loader2, Sparkles } from 'lucide-react';
import { api, ApiError } from '../../lib/api';

export type ToolPlatform = 'instagram' | 'tiktok' | 'x' | 'linkedin' | 'facebook' | 'threads';

export const PLATFORMS: { key: ToolPlatform; label: string }[] = [
  { key: 'instagram', label: 'Instagram' },
  { key: 'tiktok', label: 'TikTok' },
  { key: 'x', label: 'X (Twitter)' },
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'threads', label: 'Threads' },
];

/** Calls an /api/tools endpoint, surfacing the daily-limit state separately. */
export function useToolCall<T>(path: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [limitReached, setLimitReached] = useState(false);
  const [data, setData] = useState<T | null>(null);

  async function run(body: Record<string, unknown>) {
    setLoading(true);
    setError(null);
    try {
      const result = await api<T>(path, { method: 'POST', body: JSON.stringify(body) });
      setData(result);
    } catch (err) {
      if (err instanceof ApiError && (err.status === 429 || err.code === 'TOOL_LIMIT_REACHED')) {
        setLimitReached(true);
      } else if (err instanceof ApiError) {
        setError(err.message || 'Algo salió mal. Inténtalo de nuevo.');
      } else {
        setError('No se pudo conectar. Inténtalo de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  }

  return { run, loading, error, limitReached, data };
}

export function PlatformSelect({
  value,
  onChange,
}: {
  value: ToolPlatform;
  onChange: (p: ToolPlatform) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {PLATFORMS.map((p) => (
        <button
          key={p.key}
          type="button"
          onClick={() => onChange(p.key)}
          className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition ${
            value === p.key ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

export function GenerateButton({ loading, label }: { loading: boolean; label: string }) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
    >
      {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
      {loading ? 'Generando…' : label}
    </button>
  );
}

export function CopyButton({ text, className = '' }: { text: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard?.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
      className={`inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50 transition ${className}`}
    >
      {copied ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
      {copied ? 'Copiado' : 'Copiar'}
    </button>
  );
}

/** Shown when the daily free quota (3/IP) is exhausted. */
export function LimitReached() {
  return (
    <div className="rounded-2xl border border-brand-200 bg-gradient-to-br from-brand-50 to-white p-6 text-center">
      <Sparkles size={26} className="mx-auto text-brand-600" />
      <h3 className="mt-3 text-lg font-bold text-slate-900">Has agotado tus usos gratis de hoy</h3>
      <p className="mt-1.5 text-sm text-slate-600 max-w-md mx-auto">
        Crea tu cuenta en Zettapost para generar sin límite, programar tus publicaciones y mucho más. 3 días de prueba gratis, cancela cuando quieras.
      </p>
      <Link to="/register" className="btn-primary mt-5 inline-flex items-center gap-1.5">
        Crear cuenta gratis
        <ArrowRight size={15} />
      </Link>
    </div>
  );
}

/** Wraps a result block with a soft "publish it with Zettapost" funnel nudge. */
export function ResultFunnel({ children }: { children: ReactNode }) {
  return (
    <div className="mt-6">
      {children}
      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-slate-50 border border-slate-200 px-4 py-3">
        <span className="text-sm text-slate-600">¿Te gusta? Prográmalo y publícalo en todas tus redes con Zettapost.</span>
        <Link to="/register" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 hover:text-brand-800">
          Empezar gratis
          <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
}

export function ErrorNote({ message }: { message: string }) {
  return <p className="mt-4 rounded-lg bg-red-50 border border-red-200 px-4 py-2.5 text-sm text-red-700">{message}</p>;
}
